export const TOKEN_DECIMALS = 6;
export const TOKEN_SCALE = 1000000n;
export const OPEN_ENDED_BAND_END = (1n << 64n) - 1n;

export const CURVE_BANDS = [
  {
    endTokens: 3000n,
    startPriceLamports: 1000000n,
    slopeLamportsPerToken: 400n,
  },
  {
    endTokens: 10000n,
    startPriceLamports: 2200000n,
    slopeLamportsPerToken: 700n,
  },
  {
    endTokens: 20000n,
    startPriceLamports: 7100000n,
    slopeLamportsPerToken: 1200n,
  },
  {
    endTokens: OPEN_ENDED_BAND_END,
    startPriceLamports: 19100000n,
    slopeLamportsPerToken: 1800n,
  },
].map((band) => ({
  ...band,
  endTokensAtomic:
    band.endTokens === OPEN_ENDED_BAND_END ? null : band.endTokens * TOKEN_SCALE,
}));

function getBandStartTokensAtomic(bandIndex) {
  if (bandIndex <= 0) {
    return 0n;
  }

  return CURVE_BANDS[bandIndex - 1].endTokensAtomic;
}

export function normalizeBigIntValue(value, fallback = 0n) {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const normalizedValue = String(value).trim();

  if (!/^\d+$/.test(normalizedValue)) {
    return fallback;
  }

  return window.BigInt(normalizedValue);
}

export function getFeeBasisPoints(globalConfig) {
  const platformFeeBps = normalizeBigIntValue(globalConfig?.platform_fee_bps);
  const dumpFeeBps = normalizeBigIntValue(globalConfig?.dump_fee_bps);
  const totalFeeBps = platformFeeBps + dumpFeeBps;

  return totalFeeBps > 10000n ? 10000n : totalFeeBps;
}

export function applyFeeReduction(amountLamports, feeBasisPoints) {
  if (amountLamports <= 0n) {
    return 0n;
  }

  const safeFeeBasisPoints = feeBasisPoints > 10000n ? 10000n : feeBasisPoints;

  return (amountLamports * (10000n - safeFeeBasisPoints)) / 10000n;
}

function getBandIndexForSupply(supplyAtomic) {
  const normalizedSupply = supplyAtomic < 0n ? 0n : supplyAtomic;

  for (let index = 0; index < CURVE_BANDS.length; index += 1) {
    const band = CURVE_BANDS[index];

    if (band.endTokensAtomic === null || normalizedSupply < band.endTokensAtomic) {
      return index;
    }
  }

  return CURVE_BANDS.length - 1;
}

export function getMarginalPriceLamports(supplyAtomic, boostLamports = 0n) {
  const normalizedSupply = supplyAtomic < 0n ? 0n : supplyAtomic;
  const bandIndex = getBandIndexForSupply(normalizedSupply);
  const band = CURVE_BANDS[bandIndex];
  const bandStartTokensAtomic = getBandStartTokensAtomic(bandIndex);
  const relativeTokensAtomic = normalizedSupply - bandStartTokensAtomic;

  return (
    band.startPriceLamports +
    boostLamports +
    (band.slopeLamportsPerToken * relativeTokensAtomic) / TOKEN_SCALE
  );
}

function integrateBandRange(relativeStartAtomic, relativeEndAtomic, band, boostLamports) {
  if (relativeEndAtomic <= relativeStartAtomic) {
    return 0n;
  }

  const deltaAtomic = relativeEndAtomic - relativeStartAtomic;
  const baseArea = ((band.startPriceLamports + boostLamports) * deltaAtomic) / TOKEN_SCALE;
  const slopeArea =
    (band.slopeLamportsPerToken * (relativeEndAtomic * relativeEndAtomic - relativeStartAtomic * relativeStartAtomic)) /
    (2n * TOKEN_SCALE * TOKEN_SCALE);

  return baseArea + slopeArea;
}

export function integrateBuyCurve(currentSupplyAtomic, tokenAmountAtomic, boostLamports = 0n) {
  if (tokenAmountAtomic <= 0n) {
    return 0n;
  }

  let remainingTokensAtomic = tokenAmountAtomic;
  let runningSupplyAtomic = currentSupplyAtomic < 0n ? 0n : currentSupplyAtomic;
  let totalLamports = 0n;

  while (remainingTokensAtomic > 0n) {
    const bandIndex = getBandIndexForSupply(runningSupplyAtomic);
    const band = CURVE_BANDS[bandIndex];
    const bandStartTokensAtomic = getBandStartTokensAtomic(bandIndex);
    const bandEndTokensAtomic = band.endTokensAtomic;
    const availableInBandAtomic =
      bandEndTokensAtomic === null ? remainingTokensAtomic : bandEndTokensAtomic - runningSupplyAtomic;
    const stepTokensAtomic = remainingTokensAtomic < availableInBandAtomic ? remainingTokensAtomic : availableInBandAtomic;
    const relativeStartAtomic = runningSupplyAtomic - bandStartTokensAtomic;
    const relativeEndAtomic = relativeStartAtomic + stepTokensAtomic;

    totalLamports += integrateBandRange(relativeStartAtomic, relativeEndAtomic, band, boostLamports);
    runningSupplyAtomic += stepTokensAtomic;
    remainingTokensAtomic -= stepTokensAtomic;
  }

  return totalLamports;
}

export function integrateSellCurve(currentSupplyAtomic, tokenAmountAtomic, boostLamports = 0n) {
  if (tokenAmountAtomic <= 0n || currentSupplyAtomic <= 0n) {
    return 0n;
  }

  let remainingTokensAtomic = tokenAmountAtomic > currentSupplyAtomic ? currentSupplyAtomic : tokenAmountAtomic;
  let runningSupplyAtomic = currentSupplyAtomic;
  let totalLamports = 0n;

  while (remainingTokensAtomic > 0n && runningSupplyAtomic > 0n) {
    const bandIndex = getBandIndexForSupply(runningSupplyAtomic - 1n);
    const band = CURVE_BANDS[bandIndex];
    const bandStartTokensAtomic = getBandStartTokensAtomic(bandIndex);
    const availableInBandAtomic = runningSupplyAtomic - bandStartTokensAtomic;
    const stepTokensAtomic = remainingTokensAtomic < availableInBandAtomic ? remainingTokensAtomic : availableInBandAtomic;
    const relativeStartAtomic = runningSupplyAtomic - stepTokensAtomic - bandStartTokensAtomic;
    const relativeEndAtomic = runningSupplyAtomic - bandStartTokensAtomic;

    totalLamports += integrateBandRange(relativeStartAtomic, relativeEndAtomic, band, boostLamports);
    runningSupplyAtomic -= stepTokensAtomic;
    remainingTokensAtomic -= stepTokensAtomic;
  }

  return totalLamports;
}

export function estimateBuyTokenAmount(currentSupplyAtomic, reserveLamportsIn, boostLamports = 0n) {
  if (reserveLamportsIn <= 0n) {
    return 0n;
  }

  let low = 0n;
  let high = TOKEN_SCALE;

  for (let index = 0; index < 64; index += 1) {
    const estimatedCost = integrateBuyCurve(currentSupplyAtomic, high, boostLamports);

    if (estimatedCost >= reserveLamportsIn) {
      break;
    }

    high *= 2n;
  }

  while (low < high) {
    const mid = (low + high + 1n) / 2n;
    const estimatedCost = integrateBuyCurve(currentSupplyAtomic, mid, boostLamports);

    if (estimatedCost <= reserveLamportsIn) {
      low = mid;
    } else {
      high = mid - 1n;
    }
  }

  return low;
}